import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, MenuItem, Button, IconButton, LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { accountingAPI } from '../../services/api';
import { money, label, errorText } from '../../utils/format';
import { fmtDate } from '../../utils/dates';
import { MiniStat } from './common';

function usageColor(p) {
  if (p === null || p === undefined) return '#90a4ae';
  if (p > 100) return '#d32f2f';
  if (p >= 80) return '#ef6c00';
  return '#00897b';
}

export default function BudgetsTab({ expenseCategories, notify }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: '', amount: '', notes: '' });
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try { setData((await accountingAPI.getBudgets()).data); }
    catch (e) { notify(errorText(e, 'Could not load budgets'), 'error'); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const choices = [...new Set(['Salaries', ...expenseCategories, ...(data?.rows || []).map((r) => r.category)])];

  const openDialog = (row = null) => {
    setForm(row ? { category: row.category, amount: Number(row.budget) || '', notes: row.notes || '' } : { category: '', amount: '', notes: '' });
    setOpen(true);
  };

  const save = async () => {
    try {
      await accountingAPI.saveBudget({ category: form.category, amount: form.amount === '' ? null : Number(form.amount), notes: form.notes });
      notify('Budget saved');
      setOpen(false);
      load();
    } catch (e) { notify(errorText(e, 'Could not save the budget'), 'error'); }
  };

  const remove = async () => {
    try { await accountingAPI.deleteBudget(confirm.id); notify('Budget removed'); setConfirm(null); load(); }
    catch (e) { notify(errorText(e, 'Could not remove'), 'error'); }
  };

  if (!data) return <Typography color="text.secondary">Loading...</Typography>;
  if (!data.year) return <Alert severity="info">Set an active academic year under Programs &amp; Batches to use budgets.</Alert>;

  const totalBudget = Number(data.totalBudget || 0);
  const totalSpent = Number(data.totalSpent || 0);
  const overall = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : null;
  const over = data.rows.filter((r) => r.percentUsed !== null && Number(r.percentUsed) > 100).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Budget for {data.year.name}</Typography>
          <Typography variant="caption" color="text.secondary">Spending counted from {fmtDate(data.year.startDate)} to {fmtDate(data.year.endDate)}. Salaries are counted when paid.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Set a budget</Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><MiniStat label="Total budget" value={money(totalBudget)} /></Grid>
        <Grid item xs={6} md={3}><MiniStat label="Spent so far" value={money(totalSpent)} color="#ef6c00" bg="#fff3e0" /></Grid>
        <Grid item xs={6} md={3}><MiniStat label="Remaining" value={money(totalBudget - totalSpent)} color="#00796b" bg="#e8f5e9" /></Grid>
        <Grid item xs={6} md={3}><MiniStat label="Categories over budget" value={over} color={over ? '#d32f2f' : '#00796b'} bg={over ? '#ffebee' : '#e8f5e9'} /></Grid>
      </Grid>
      {overall !== null && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={700}>Overall budget used</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color: usageColor(overall) }}>{overall.toFixed(1)}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={Math.min(100, overall)} sx={{ height: 10, borderRadius: 5, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: usageColor(overall) } }} />
        </Paper>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Category</TableCell><TableCell align="right">Budget</TableCell><TableCell align="right">Spent</TableCell><TableCell align="right">Remaining</TableCell>
              <TableCell sx={{ width: 220 }}>Used</TableCell><TableCell align="center">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {data.rows.map((r) => {
                const p = r.percentUsed === null ? null : Number(r.percentUsed);
                return (
                  <TableRow key={r.category} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{label(r.category)} {p === null && <Chip size="small" label="No budget set" sx={{ ml: 1 }} />}</TableCell>
                    <TableCell align="right">{Number(r.budget) ? money(r.budget) : '-'}</TableCell>
                    <TableCell align="right" sx={{ color: '#ef6c00', fontWeight: 700 }}>{money(r.spent)}</TableCell>
                    <TableCell align="right" sx={{ color: Number(r.remaining) < 0 ? '#d32f2f' : '#00796b', fontWeight: 700 }}>{Number(r.budget) ? money(r.remaining) : '-'}</TableCell>
                    <TableCell>
                      {p !== null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={Math.min(100, p)} sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: usageColor(p) } }} />
                          <Typography variant="caption" fontWeight={700} sx={{ color: usageColor(p), minWidth: 44 }}>{p.toFixed(0)}%</Typography>
                        </Box>
                      ) : <Typography variant="caption" color="text.secondary">-</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openDialog(r)}><EditIcon fontSize="small" /></IconButton>
                      {r.id && <IconButton size="small" color="error" onClick={() => setConfirm(r)}><DeleteIcon fontSize="small" /></IconButton>}
                    </TableCell>
                  </TableRow>
                );
              })}
              {data.rows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>No budgets yet. Set a yearly budget for each spending category.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>Set a budget</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField select fullWidth size="small" label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} sx={{ mt: 1, mb: 2 }}>
            {choices.map((c) => <MenuItem key={c} value={c}>{label(c)}</MenuItem>)}
          </TextField>
          <TextField fullWidth size="small" type="number" label="Yearly budget (₹)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth size="small" label="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" onClick={save}>Save</Button></DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove this budget?</DialogTitle>
        <DialogContent><Typography>The budget for {label(confirm?.category)} will be removed. Past spending is not affected.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Remove</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
